/**
 * Classifier Threshold Lab
 * Interactive visualization of decision threshold tuning for binary classifiers
 */
import {
  SCENARIOS,
  computeAUC,
  computeMetrics,
  computePRPoints,
  computeROCPoints,
  findOptimalThreshold,
  generateScores,
  type ClassifierMetrics,
  type OptimizeStrategy,
  type PRPoint,
  type ROCPoint,
  type ScenarioConfig,
} from '../../lib/labs/classifier-threshold';

// ===== DOM Elements =====
const distributionCanvas = document.getElementById(
  'distribution-canvas'
) as HTMLCanvasElement;
const rocCanvas = document.getElementById('roc-canvas') as HTMLCanvasElement;
const prCanvas = document.getElementById('pr-canvas') as HTMLCanvasElement;

const thresholdSlider = document.getElementById('threshold-slider') as HTMLInputElement;
const thresholdValue = document.getElementById('threshold-value') as HTMLElement;

const cmTp = document.getElementById('cm-tp') as HTMLElement;
const cmFp = document.getElementById('cm-fp') as HTMLElement;
const cmTn = document.getElementById('cm-tn') as HTMLElement;
const cmFn = document.getElementById('cm-fn') as HTMLElement;

const statAccuracy = document.getElementById('stat-accuracy') as HTMLElement;
const statPrecision = document.getElementById('stat-precision') as HTMLElement;
const statRecall = document.getElementById('stat-recall') as HTMLElement;
const statF1 = document.getElementById('stat-f1') as HTMLElement;
const statFpr = document.getElementById('stat-fpr') as HTMLElement;

const rocAucLabel = document.getElementById('roc-auc') as HTMLElement;
const prAucLabel = document.getElementById('pr-auc') as HTMLElement;

const prevalenceSlider = document.getElementById('prevalence-slider') as HTMLInputElement;
const prevalenceValue = document.getElementById('prevalence-value') as HTMLElement;
const noiseSlider = document.getElementById('noise-slider') as HTMLInputElement;
const noiseValue = document.getElementById('noise-value') as HTMLElement;

const scenarioButtons = document.querySelectorAll<HTMLButtonElement>('[data-scenario]');
const optimizeRadios = document.querySelectorAll<HTMLInputElement>(
  'input[name="optimize"]'
);

// ===== State =====
let currentConfig: ScenarioConfig = { ...SCENARIOS[0] };
let positives: number[] = [];
let negatives: number[] = [];
let threshold = 0.5;
let rocPoints: ROCPoint[] = [];
let prPoints: PRPoint[] = [];
let animationId: number | null = null;

// ===== Canvas Colors =====
const COLORS = {
  positive: '#34d399', // emerald-400
  negative: '#f87171', // red-400
  threshold: '#fbbf24', // amber-400
  rocCurve: '#60a5fa', // blue-400
  prCurve: '#c084fc', // purple-400
  rocDot: '#fbbf24',
  prDot: '#fbbf24',
  diagonal: 'rgba(148,163,184,0.3)', // slate-400
  axis: 'rgba(148,163,184,0.5)',
  label: 'rgba(148,163,184,0.8)',
  grid: 'rgba(148,163,184,0.1)',
};

// ===== Initialization =====
function init(): void {
  loadScenario(SCENARIOS[0]);
  bindEvents();
  setupCanvasSizing();
}

function loadScenario(config: ScenarioConfig): void {
  currentConfig = { ...config };
  threshold = config.defaultThreshold;
  thresholdSlider.value = String(threshold);
  prevalenceSlider.value = String(Math.round(config.prevalence * 100));
  prevalenceValue.textContent = `${Math.round(config.prevalence * 100)}%`;

  // Derive noise from the config's std values relative to mean separation
  const meanSep = Math.abs(config.positiveMean - config.negativeMean);
  const avgStd = (config.positiveStd + config.negativeStd) / 2;
  const noiseRatio = Math.min(100, Math.round((avgStd / Math.max(meanSep, 0.01)) * 100));
  noiseSlider.value = String(noiseRatio);
  noiseValue.textContent = `${noiseRatio}%`;

  // Clear optimize radio
  for (const radio of optimizeRadios) {
    radio.checked = false;
  }

  // Highlight active scenario button
  for (const btn of scenarioButtons) {
    btn.classList.toggle('active', btn.dataset.scenario === config.id);
  }

  regenerateData();
}

function regenerateData(): void {
  const scores = generateScores(currentConfig);
  positives = scores.positives;
  negatives = scores.negatives;
  rocPoints = computeROCPoints(positives, negatives);
  prPoints = computePRPoints(positives, negatives);
  updateAll();
}

function updateAll(): void {
  const metrics = computeMetrics(positives, negatives, threshold);
  updateConfusionMatrix(metrics);
  updateStats(metrics);
  updateAUCLabels();
  drawDistribution();
  drawROC(metrics);
  drawPR(metrics);
}

// ===== DOM Updates =====
function updateConfusionMatrix(m: ClassifierMetrics): void {
  cmTp.textContent = String(m.tp);
  cmFp.textContent = String(m.fp);
  cmTn.textContent = String(m.tn);
  cmFn.textContent = String(m.fn);
}

function updateStats(m: ClassifierMetrics): void {
  statAccuracy.textContent = m.accuracy.toFixed(3);
  statPrecision.textContent = m.precision.toFixed(3);
  statRecall.textContent = m.recall.toFixed(3);
  statF1.textContent = m.f1.toFixed(3);
  statFpr.textContent = m.fpr.toFixed(3);
  thresholdValue.textContent = threshold.toFixed(3);
}

function updateAUCLabels(): void {
  const rocAuc = computeAUC(rocPoints.map((p) => ({ x: p.fpr, y: p.tpr })));
  const prAuc = computeAUC(prPoints.map((p) => ({ x: p.recall, y: p.precision })));
  rocAucLabel.textContent = `AUC = ${rocAuc.toFixed(3)}`;
  prAucLabel.textContent = `AUC = ${prAuc.toFixed(3)}`;
}

// ===== Canvas Sizing =====
function setupCanvasSizing(): void {
  const canvases = [distributionCanvas, rocCanvas, prCanvas];
  const observer = new ResizeObserver(() => {
    for (const canvas of canvases) {
      sizeCanvas(canvas);
    }
    updateAll();
  });
  for (const canvas of canvases) {
    sizeCanvas(canvas);
    observer.observe(canvas.parentElement!);
  }
}

function sizeCanvas(canvas: HTMLCanvasElement): void {
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
}

// ===== Drawing: Distribution Histogram =====
function drawDistribution(): void {
  const ctx = distributionCanvas.getContext('2d');
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  const w = distributionCanvas.width;
  const h = distributionCanvas.height;
  ctx.clearRect(0, 0, w, h);

  const pad = { top: 20 * dpr, right: 20 * dpr, bottom: 40 * dpr, left: 50 * dpr };
  const plotW = w - pad.left - pad.right;
  const plotH = h - pad.top - pad.bottom;

  // Build histogram bins
  const bins = 50;
  const negCounts = new Float64Array(bins);
  const posCounts = new Float64Array(bins);

  for (const v of negatives) {
    const idx = Math.min(bins - 1, Math.max(0, Math.floor(v * bins)));
    negCounts[idx]++;
  }
  for (const v of positives) {
    const idx = Math.min(bins - 1, Math.max(0, Math.floor(v * bins)));
    posCounts[idx]++;
  }

  const maxCount = Math.max(1, ...negCounts, ...posCounts);
  const barW = plotW / bins;

  // Grid lines
  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = dpr;
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + (plotH * i) / 4;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + plotW, y);
    ctx.stroke();
  }

  // Negative bars
  ctx.fillStyle = 'rgba(248,113,113,0.4)';
  for (let i = 0; i < bins; i++) {
    const barH = (negCounts[i] / maxCount) * plotH;
    const x = pad.left + i * barW;
    const y = pad.top + plotH - barH;
    ctx.fillRect(x, y, barW - dpr, barH);
  }

  // Positive bars
  ctx.fillStyle = 'rgba(52,211,153,0.4)';
  for (let i = 0; i < bins; i++) {
    const barH = (posCounts[i] / maxCount) * plotH;
    const x = pad.left + i * barW;
    const y = pad.top + plotH - barH;
    ctx.fillRect(x, y, barW - dpr, barH);
  }

  // Threshold line
  const threshX = pad.left + threshold * plotW;
  ctx.strokeStyle = COLORS.threshold;
  ctx.lineWidth = 2 * dpr;
  ctx.setLineDash([6 * dpr, 4 * dpr]);
  ctx.beginPath();
  ctx.moveTo(threshX, pad.top);
  ctx.lineTo(threshX, pad.top + plotH);
  ctx.stroke();
  ctx.setLineDash([]);

  // Threshold label
  ctx.fillStyle = COLORS.threshold;
  ctx.font = `${11 * dpr}px ui-monospace, monospace`;
  ctx.textAlign = 'center';
  ctx.fillText(`t = ${threshold.toFixed(3)}`, threshX, pad.top - 5 * dpr);

  // Axes
  ctx.strokeStyle = COLORS.axis;
  ctx.lineWidth = dpr;
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top);
  ctx.lineTo(pad.left, pad.top + plotH);
  ctx.lineTo(pad.left + plotW, pad.top + plotH);
  ctx.stroke();

  // X-axis labels
  ctx.fillStyle = COLORS.label;
  ctx.font = `${10 * dpr}px ui-sans-serif, sans-serif`;
  ctx.textAlign = 'center';
  for (let i = 0; i <= 10; i++) {
    const x = pad.left + (i / 10) * plotW;
    ctx.fillText((i / 10).toFixed(1), x, pad.top + plotH + 16 * dpr);
  }
  ctx.fillText('Score', pad.left + plotW / 2, pad.top + plotH + 32 * dpr);

  // Y-axis label
  ctx.save();
  ctx.translate(14 * dpr, pad.top + plotH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('Count', 0, 0);
  ctx.restore();

  // Legend
  const legendX = pad.left + plotW - 120 * dpr;
  const legendY = pad.top + 14 * dpr;
  ctx.font = `${11 * dpr}px ui-sans-serif, sans-serif`;

  ctx.fillStyle = 'rgba(248,113,113,0.6)';
  ctx.fillRect(legendX, legendY - 8 * dpr, 12 * dpr, 10 * dpr);
  ctx.fillStyle = COLORS.label;
  ctx.textAlign = 'left';
  ctx.fillText('Negative', legendX + 16 * dpr, legendY);

  ctx.fillStyle = 'rgba(52,211,153,0.6)';
  ctx.fillRect(legendX, legendY + 8 * dpr, 12 * dpr, 10 * dpr);
  ctx.fillStyle = COLORS.label;
  ctx.fillText('Positive', legendX + 16 * dpr, legendY + 16 * dpr);
}

// ===== Drawing: ROC Curve =====
function drawROC(metrics: ClassifierMetrics): void {
  const ctx = rocCanvas.getContext('2d');
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  const w = rocCanvas.width;
  const h = rocCanvas.height;
  ctx.clearRect(0, 0, w, h);

  const pad = { top: 20 * dpr, right: 20 * dpr, bottom: 40 * dpr, left: 50 * dpr };
  const plotW = w - pad.left - pad.right;
  const plotH = h - pad.top - pad.bottom;

  drawPlotFrame(ctx, pad, plotW, plotH, dpr, 'False Positive Rate', 'True Positive Rate');

  // Diagonal (random classifier)
  ctx.strokeStyle = COLORS.diagonal;
  ctx.lineWidth = dpr;
  ctx.setLineDash([4 * dpr, 4 * dpr]);
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top + plotH);
  ctx.lineTo(pad.left + plotW, pad.top);
  ctx.stroke();
  ctx.setLineDash([]);

  // ROC curve
  ctx.strokeStyle = COLORS.rocCurve;
  ctx.lineWidth = 2 * dpr;
  ctx.beginPath();
  for (let i = 0; i < rocPoints.length; i++) {
    const x = pad.left + rocPoints[i].fpr * plotW;
    const y = pad.top + plotH - rocPoints[i].tpr * plotH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Current operating point
  const dotX = pad.left + metrics.fpr * plotW;
  const dotY = pad.top + plotH - metrics.tpr * plotH;
  ctx.fillStyle = COLORS.rocDot;
  ctx.beginPath();
  ctx.arc(dotX, dotY, 5 * dpr, 0, Math.PI * 2);
  ctx.fill();
}

// ===== Drawing: PR Curve =====
function drawPR(metrics: ClassifierMetrics): void {
  const ctx = prCanvas.getContext('2d');
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  const w = prCanvas.width;
  const h = prCanvas.height;
  ctx.clearRect(0, 0, w, h);

  const pad = { top: 20 * dpr, right: 20 * dpr, bottom: 40 * dpr, left: 50 * dpr };
  const plotW = w - pad.left - pad.right;
  const plotH = h - pad.top - pad.bottom;

  drawPlotFrame(ctx, pad, plotW, plotH, dpr, 'Recall', 'Precision');

  // PR curve
  ctx.strokeStyle = COLORS.prCurve;
  ctx.lineWidth = 2 * dpr;
  ctx.beginPath();
  let started = false;
  for (let i = 0; i < prPoints.length; i++) {
    const p = prPoints[i];
    if (p.precision === 0 && p.recall === 0) continue;
    const x = pad.left + p.recall * plotW;
    const y = pad.top + plotH - p.precision * plotH;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();

  // Current operating point
  const dotX = pad.left + metrics.recall * plotW;
  const dotY = pad.top + plotH - metrics.precision * plotH;
  ctx.fillStyle = COLORS.prDot;
  ctx.beginPath();
  ctx.arc(dotX, dotY, 5 * dpr, 0, Math.PI * 2);
  ctx.fill();
}

// ===== Drawing: Common Plot Frame =====
function drawPlotFrame(
  ctx: CanvasRenderingContext2D,
  pad: { top: number; right: number; bottom: number; left: number },
  plotW: number,
  plotH: number,
  dpr: number,
  xLabel: string,
  yLabel: string
): void {
  // Grid
  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = dpr;
  for (let i = 0; i <= 4; i++) {
    const frac = i / 4;
    // Horizontal
    const y = pad.top + plotH * (1 - frac);
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + plotW, y);
    ctx.stroke();
    // Vertical
    const x = pad.left + plotW * frac;
    ctx.beginPath();
    ctx.moveTo(x, pad.top);
    ctx.lineTo(x, pad.top + plotH);
    ctx.stroke();
  }

  // Axes
  ctx.strokeStyle = COLORS.axis;
  ctx.lineWidth = dpr;
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top);
  ctx.lineTo(pad.left, pad.top + plotH);
  ctx.lineTo(pad.left + plotW, pad.top + plotH);
  ctx.stroke();

  // Tick labels
  ctx.fillStyle = COLORS.label;
  ctx.font = `${10 * dpr}px ui-sans-serif, sans-serif`;
  ctx.textAlign = 'center';
  for (let i = 0; i <= 4; i++) {
    const val = (i / 4).toFixed(2);
    const x = pad.left + (i / 4) * plotW;
    ctx.fillText(val, x, pad.top + plotH + 16 * dpr);
  }
  ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const val = (i / 4).toFixed(2);
    const y = pad.top + plotH - (i / 4) * plotH + 4 * dpr;
    ctx.fillText(val, pad.left - 6 * dpr, y);
  }

  // Axis labels
  ctx.fillStyle = COLORS.label;
  ctx.font = `${11 * dpr}px ui-sans-serif, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(xLabel, pad.left + plotW / 2, pad.top + plotH + 32 * dpr);

  ctx.save();
  ctx.translate(14 * dpr, pad.top + plotH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(yLabel, 0, 0);
  ctx.restore();
}

// ===== Event Handlers =====
function bindEvents(): void {
  // Threshold slider
  thresholdSlider.addEventListener('input', () => {
    threshold = parseFloat(thresholdSlider.value);
    // Clear optimize radio on manual drag
    for (const radio of optimizeRadios) {
      radio.checked = false;
    }
    updateAll();
  });

  // Scenario buttons
  for (const btn of scenarioButtons) {
    btn.addEventListener('click', () => {
      const id = btn.dataset.scenario!;
      const scenario = SCENARIOS.find((s) => s.id === id);
      if (scenario) loadScenario(scenario);
    });
  }

  // Prevalence slider
  prevalenceSlider.addEventListener('input', () => {
    const pct = parseInt(prevalenceSlider.value, 10);
    prevalenceValue.textContent = `${pct}%`;
    currentConfig.prevalence = pct / 100;
    regenerateData();
  });

  // Noise slider
  noiseSlider.addEventListener('input', () => {
    const noise = parseInt(noiseSlider.value, 10);
    noiseValue.textContent = `${noise}%`;

    // Map noise % to std deviation scaling
    const baseSep = Math.abs(currentConfig.positiveMean - currentConfig.negativeMean);
    const noiseFactor = noise / 100;
    const newStd = Math.max(0.02, baseSep * noiseFactor);
    currentConfig.positiveStd = newStd;
    currentConfig.negativeStd = newStd;
    regenerateData();
  });

  // Optimize radio buttons
  for (const radio of optimizeRadios) {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      const strategy = radio.value as OptimizeStrategy;
      const optimal = findOptimalThreshold(positives, negatives, strategy);
      animateThreshold(optimal);
    });
  }
}

// ===== Threshold Animation =====
function animateThreshold(target: number): void {
  if (animationId !== null) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }

  const start = threshold;
  const duration = 400;
  const startTime = performance.now();

  function step(now: number): void {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / duration);
    // Ease out cubic
    const eased = 1 - Math.pow(1 - progress, 3);
    threshold = start + (target - start) * eased;
    thresholdSlider.value = String(threshold);
    updateAll();

    if (progress < 1) {
      animationId = requestAnimationFrame(step);
    } else {
      animationId = null;
    }
  }

  animationId = requestAnimationFrame(step);
}

// ===== Start =====
init();
